import React, { useState, useRef } from 'react';
import { C, Btn } from '../utils/styles';

// ── CRM field definitions ────────────────────────────────────
const CRM_FIELDS = [
  { key:'name',    label:'Full Name',   required:true  },
  { key:'email',   label:'Email',       required:false },
  { key:'phone',   label:'Phone',       required:false },
  { key:'company', label:'Company',     required:false },
  { key:'source',  label:'Lead Source', required:false },
  { key:'stage',   label:'Stage',       required:false },
  { key:'value',   label:'Deal Value',  required:false },
  { key:'tags',    label:'Tags',        required:false },
  { key:'notes',   label:'Notes',       required:false },
];

// ── Robust CSV parser (handles quotes, commas in values) ─────
const parseCSV = (text) => {
  const lines = []; // eslint-disable-line
  let cur = '', inQuote = false;
  const rows = [];
  let curRow = [];

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const next = text[i+1];

    if (ch === '"') {
      if (inQuote && next === '"') { cur += '"'; i++; }
      else inQuote = !inQuote;
    } else if (ch === ',' && !inQuote) {
      curRow.push(cur.trim());
      cur = '';
    } else if ((ch === '\n' || ch === '\r') && !inQuote) {
      if (ch === '\r' && next === '\n') i++;
      curRow.push(cur.trim());
      if (curRow.some(c => c !== '')) rows.push(curRow);
      curRow = [];
      cur = '';
    } else {
      cur += ch;
    }
  }
  if (cur || curRow.length) { curRow.push(cur.trim()); if (curRow.some(c=>c!=='')) rows.push(curRow); }

  return rows;
};

// ── Auto-match CSV header to CRM field ───────────────────────
const autoMatch = (header, customFields = []) => {
  const h = header.toLowerCase().replace(/[\s_-]/g,'');
  const allFields = [
    ...CRM_FIELDS,
    ...customFields.map(f => ({ key:`cf_${f.field_key}`, label:f.name, required:false })),
  ];
  for (const f of allFields) {
    const fk = f.key.toLowerCase().replace(/[\s_-]/g,'');
    const fl = f.label.toLowerCase().replace(/[\s_-]/g,'');
    if (h===fk || h===fl || h.includes(fk) || fk.includes(h)) return f.key;
  }
  return '';
};

export default function CsvImporter({ customFields = [], onComplete, onClose }) {
  const fileRef   = useRef();
  const [step,    setStep]    = useState(1); // 1=upload, 2=map, 3=review, 4=done
  const [headers, setHeaders] = useState([]);
  const [rows,    setRows]    = useState([]);
  const [mapping, setMapping] = useState({});  // { csvHeader: crmFieldKey }
  const [result,  setResult]  = useState(null);
  const [loading, setLoading] = useState(false);
  const [fileName,setFileName]= useState('');

  const allCrmFields = [
    ...CRM_FIELDS,
    ...customFields.map(f => ({ key:`cf_${f.field_key}`, label:f.name, required:false })),
  ];

  // ── Step 1: Parse uploaded CSV ───────────────────────────
  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    const text    = await file.text();
    const parsed  = parseCSV(text);
    if (parsed.length < 2) return alert('CSV file appears empty or invalid.');

    const hdrs = parsed[0];
    const data = parsed.slice(1).filter(r => r.some(c => c !== ''));

    setHeaders(hdrs);
    setRows(data);

    // Auto-match headers
    const auto = {};
    hdrs.forEach(h => { auto[h] = autoMatch(h, customFields); });
    setMapping(auto);
    setStep(2);
    e.target.value = '';
  };

  // ── Build preview rows with mapping applied ──────────────
  const buildMappedRows = () => {
    return rows.map(row => {
      const obj = {};
      headers.forEach((h, i) => {
        const crmKey = mapping[h];
        if (crmKey && row[i]) obj[crmKey] = row[i];
      });
      return obj;
    }).filter(r => r.name || r.email);
  };

  const mappedRows = buildMappedRows();

  // ── Step 3 → 4: Run import ───────────────────────────────
  const handleImport = async () => {
    setLoading(true);
    try {
      // Dynamic import of client to avoid circular deps
      const { bulkUpsertLeads } = await import('../api/client');
      const { data } = await bulkUpsertLeads(mappedRows);
      setResult(data);
      setStep(4);
    } catch (err) {
      alert(err.response?.data?.error || 'Import failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const STEP_LABELS = ['Upload CSV', 'Map Columns', 'Review', 'Done'];

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ background:'#fff', borderRadius:16, width:'100%', maxWidth:780, maxHeight:'90vh', overflow:'hidden', display:'flex', flexDirection:'column', boxShadow:'0 25px 50px rgba(0,0,0,0.3)' }}>

        {/* Header */}
        <div style={{ padding:'20px 24px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <div>
            <div style={{ fontWeight:700, fontSize:16, color:C.dark }}>Bulk Import Contacts</div>
            {fileName && <div style={{ fontSize:12, color:C.muted, marginTop:2 }}>{fileName} · {rows.length} rows detected</div>}
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', fontSize:20, color:C.faint }}>
            <i className="ti ti-x" />
          </button>
        </div>

        {/* Step progress */}
        <div style={{ padding:'16px 24px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', gap:0, flexShrink:0 }}>
          {STEP_LABELS.map((label, i) => {
            const s = i + 1;
            const done   = step > s;
            const active = step === s;
            return (
              <React.Fragment key={i}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <div style={{ width:28, height:28, borderRadius:'50%', background:done?C.success:active?C.primary:'#f3f4f6', display:'flex', alignItems:'center', justifyContent:'center', border:`2px solid ${done?C.success:active?C.primary:'#e5e7eb'}`, flexShrink:0 }}>
                    {done
                      ? <i className="ti ti-check" style={{ fontSize:13, color:'#fff' }} />
                      : <span style={{ fontSize:12, fontWeight:700, color:active?'#fff':C.faint }}>{s}</span>
                    }
                  </div>
                  <span style={{ fontSize:12, fontWeight:active?600:400, color:active?C.primary:done?C.success:C.faint, whiteSpace:'nowrap' }}>{label}</span>
                </div>
                {i < STEP_LABELS.length-1 && (
                  <div style={{ flex:1, height:2, background:step>s?C.success:'#e5e7eb', margin:'0 12px' }} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Content */}
        <div style={{ flex:1, overflow:'auto', padding:24 }}>

          {/* ── Step 1: Upload ── */}
          {step===1 && (
            <div style={{ textAlign:'center', padding:'40px 20px' }}>
              <div style={{ width:64, height:64, borderRadius:16, background:'var(--color-primary-light)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px' }}>
                <i className="ti ti-upload" style={{ fontSize:32, color:C.primary }} />
              </div>
              <div style={{ fontWeight:600, fontSize:16, color:C.dark, marginBottom:8 }}>Upload your CSV file</div>
              <div style={{ fontSize:13, color:C.muted, marginBottom:24, maxWidth:400, margin:'0 auto 24px' }}>
                CSV should have a header row. Supported columns: name, email, phone, company, source, stage, value, tags, notes + any custom fields.
              </div>
              <Btn onClick={() => fileRef.current.click()}>
                <i className="ti ti-file-upload" /> Choose CSV File
              </Btn>
              <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display:'none' }} onChange={handleFile} />

              {/* Template download */}
              <div style={{ marginTop:20 }}>
                <button onClick={() => {
                  const cfHeaders = customFields.map(f => f.field_key).join(',');
                  const csv = `name,email,phone,company,source,stage,value,tags${cfHeaders?','+cfHeaders:''}\nJohn Doe,john@example.com,+91 99999 00000,Company Ltd,Google Ads,New,50000,hot|SEO`;
                  const url = URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
                  const a = document.createElement('a'); a.href=url; a.download='dx-import-template.csv'; a.click();
                }} style={{ background:'none', border:'none', cursor:'pointer', color:C.primary, fontSize:13, textDecoration:'underline' }}>
                  Download template CSV
                </button>
              </div>
            </div>
          )}

          {/* ── Step 2: Map columns ── */}
          {step===2 && (
            <div>
              <div style={{ marginBottom:16 }}>
                <div style={{ fontWeight:600, fontSize:15, color:C.dark, marginBottom:4 }}>Map CSV Columns to CRM Fields</div>
                <div style={{ fontSize:13, color:C.muted }}>
                  We've auto-matched where possible. Review and adjust any columns as needed. Unmapped columns will be ignored.
                </div>
              </div>

              {/* Mapping table */}
              <div style={{ border:`1px solid ${C.border}`, borderRadius:10, overflow:'hidden', marginBottom:16 }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 40px 1fr', background:'#f9fafb', padding:'10px 16px', borderBottom:`1px solid ${C.border}` }}>
                  <div style={{ fontSize:12, fontWeight:600, color:C.muted }}>CSV COLUMN</div>
                  <div />
                  <div style={{ fontSize:12, fontWeight:600, color:C.muted }}>CRM FIELD</div>
                </div>
                {headers.map((h, i) => (
                  <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 40px 1fr', padding:'10px 16px', borderBottom:i<headers.length-1?`1px solid #f3f4f6`:'none', alignItems:'center' }}>
                    {/* CSV header */}
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <div style={{ background:'#f3f4f6', borderRadius:6, padding:'4px 10px', fontSize:13, fontWeight:500, color:C.dark }}>{h}</div>
                      {rows[0]?.[i] && (
                        <div style={{ fontSize:11, color:C.faint, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:120 }}>
                          e.g. {rows[0][i]}
                        </div>
                      )}
                    </div>

                    {/* Arrow */}
                    <div style={{ textAlign:'center' }}>
                      <i className="ti ti-arrow-right" style={{ fontSize:16, color:mapping[h]?C.primary:'#d1d5db' }} />
                    </div>

                    {/* CRM field selector */}
                    <select
                      value={mapping[h]||''}
                      onChange={e => setMapping({...mapping, [h]:e.target.value})}
                      style={{ padding:'7px 10px', borderRadius:8, border:`1px solid ${mapping[h]?C.primary:C.border}`, fontSize:13, outline:'none', background:mapping[h]?'var(--color-primary-light)':'#fff', color:mapping[h]?C.primary:C.muted }}>
                      <option value="">— Do not import —</option>
                      <optgroup label="Standard Fields">
                        {CRM_FIELDS.map(f => (
                          <option key={f.key} value={f.key}>{f.label}{f.required?' *':''}</option>
                        ))}
                      </optgroup>
                      {customFields.length > 0 && (
                        <optgroup label="Custom Fields">
                          {customFields.map(f => (
                            <option key={f.field_key} value={`cf_${f.field_key}`}>{f.name}</option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                  </div>
                ))}
              </div>

              {/* Mapping summary */}
              <div style={{ background:'#f0f9ff', border:'1px solid #bae6fd', borderRadius:8, padding:'10px 14px', fontSize:12, color:'#0369a1', marginBottom:16 }}>
                <i className="ti ti-info-circle" style={{ marginRight:6 }} />
                {Object.values(mapping).filter(Boolean).length} of {headers.length} columns mapped ·{' '}
                {mappedRows.length} rows will be imported
                {!Object.values(mapping).includes('name') && !Object.values(mapping).includes('email') && (
                  <span style={{ color:C.danger, fontWeight:600 }}> — Map at least Name or Email to continue</span>
                )}
              </div>

              <div style={{ display:'flex', gap:8 }}>
                <Btn onClick={() => setStep(3)}
                  disabled={!Object.values(mapping).some(v => v==='name'||v==='email')}>
                  <i className="ti ti-arrow-right" /> Review Import
                </Btn>
                <Btn variant="outline" onClick={() => setStep(1)}>Back</Btn>
              </div>
            </div>
          )}

          {/* ── Step 3: Review ── */}
          {step===3 && (
            <div>
              <div style={{ marginBottom:16 }}>
                <div style={{ fontWeight:600, fontSize:15, color:C.dark, marginBottom:4 }}>Review Before Importing</div>
                <div style={{ fontSize:13, color:C.muted }}>
                  Showing first 10 rows. {mappedRows.length} total contacts will be imported.
                  Existing contacts (matched by email) will be updated, not duplicated.
                </div>
              </div>

              {/* Preview table */}
              <div style={{ border:`1px solid ${C.border}`, borderRadius:10, overflow:'auto', marginBottom:16, maxHeight:320 }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                  <thead>
                    <tr style={{ background:'#f9fafb', borderBottom:`1px solid ${C.border}` }}>
                      <th style={{ padding:'8px 12px', textAlign:'left', color:C.muted, fontWeight:500 }}>#</th>
                      {Object.values(mapping).filter(Boolean).map((crmKey, i) => {
                        const field = allCrmFields.find(f => f.key===crmKey);
                        return <th key={i} style={{ padding:'8px 12px', textAlign:'left', color:C.muted, fontWeight:500, whiteSpace:'nowrap' }}>{field?.label||crmKey}</th>;
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {mappedRows.slice(0,10).map((row, i) => (
                      <tr key={i} style={{ borderBottom:'1px solid #f3f4f6' }}>
                        <td style={{ padding:'8px 12px', color:C.faint }}>{i+1}</td>
                        {Object.values(mapping).filter(Boolean).map((crmKey, j) => (
                          <td key={j} style={{ padding:'8px 12px', color:C.dark, maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                            {Array.isArray(row[crmKey]) ? row[crmKey].join(', ') : row[crmKey] || <span style={{ color:'#d1d5db' }}>—</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {mappedRows.length > 10 && (
                  <div style={{ padding:'10px 16px', fontSize:12, color:C.muted, background:'#f9fafb', borderTop:`1px solid ${C.border}` }}>
                    + {mappedRows.length - 10} more rows not shown
                  </div>
                )}
              </div>

              {/* Stats */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:16 }}>
                {[
                  { label:'Total Rows',       val:mappedRows.length,          color:C.primary, icon:'ti-users' },
                  { label:'Columns Mapped',   val:Object.values(mapping).filter(Boolean).length, color:C.green, icon:'ti-check' },
                  { label:'Skipped (no name/email)', val:rows.length-mappedRows.length, color:C.warning, icon:'ti-alert-triangle' },
                ].map((s,i) => (
                  <div key={i} style={{ background:'#f9fafb', borderRadius:10, border:`1px solid ${C.border}`, padding:'12px 16px', display:'flex', alignItems:'center', gap:10 }}>
                    <i className={`ti ${s.icon}`} style={{ fontSize:20, color:s.color }} />
                    <div>
                      <div style={{ fontWeight:700, fontSize:18, color:C.dark }}>{s.val}</div>
                      <div style={{ fontSize:11, color:C.muted }}>{s.label}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display:'flex', gap:8 }}>
                <Btn onClick={handleImport} disabled={loading||mappedRows.length===0}>
                  {loading ? (
                    <>
                      <div style={{ width:14, height:14, border:'2px solid rgba(255,255,255,0.4)', borderTop:'2px solid #fff', borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
                      Importing {mappedRows.length} contacts...
                    </>
                  ) : (
                    <><i className="ti ti-check" /> Confirm & Import {mappedRows.length} Contacts</>
                  )}
                </Btn>
                <Btn variant="outline" onClick={() => setStep(2)}>Back</Btn>
              </div>
            </div>
          )}

          {/* ── Step 4: Done ── */}
          {step===4 && result && (
            <div style={{ textAlign:'center', padding:'32px 20px' }}>
              <div style={{ width:64, height:64, borderRadius:'50%', background:'#f0fdf4', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px' }}>
                <i className="ti ti-circle-check" style={{ fontSize:36, color:C.success }} />
              </div>
              <div style={{ fontWeight:700, fontSize:20, color:C.dark, marginBottom:8 }}>Import Complete!</div>
              <div style={{ fontSize:14, color:C.muted, marginBottom:28 }}>{result.message}</div>

              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, maxWidth:400, margin:'0 auto 28px' }}>
                {[
                  { label:'Created',  val:result.created,  color:C.success, bg:'#f0fdf4' },
                  { label:'Updated',  val:result.updated,  color:C.primary, bg:'#f0f9ff' },
                  { label:'Failed',   val:result.failed,   color:result.failed>0?C.danger:C.faint, bg:result.failed>0?'#fef2f2':'#f9fafb' },
                ].map((s,i) => (
                  <div key={i} style={{ background:s.bg, borderRadius:12, padding:'16px 12px', textAlign:'center' }}>
                    <div style={{ fontWeight:700, fontSize:24, color:s.color }}>{s.val}</div>
                    <div style={{ fontSize:12, color:C.muted, marginTop:2 }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {result.errors?.length > 0 && (
                <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:8, padding:12, marginBottom:20, textAlign:'left', maxWidth:500, margin:'0 auto 20px' }}>
                  <div style={{ fontSize:12, fontWeight:600, color:'#991b1b', marginBottom:6 }}>Failed rows:</div>
                  {result.errors.slice(0,5).map((e,i) => (
                    <div key={i} style={{ fontSize:12, color:'#7f1d1d', marginBottom:2 }}>• {e.row}: {e.error}</div>
                  ))}
                  {result.errors.length>5 && <div style={{ fontSize:12, color:'#9ca3af' }}>+ {result.errors.length-5} more</div>}
                </div>
              )}

              <Btn onClick={() => { onComplete(); onClose(); }}>
                <i className="ti ti-users" /> View Contacts
              </Btn>
            </div>
          )}
        </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
